import type { ColumnSemantic } from "@lala/harness/protocol";

/**
 * Formato por semántica de columna (`PresentationColumn.semantic`).
 *
 * Es un vocabulario nuevo y propio de `presentation`: no es el mismo que
 * `formatCell` de `../dataset.ts` (ese solo sabe de `"text"|"number"|"boolean"`,
 * el tipo genérico del dataset). Aquí hay más matices --moneda, porcentaje,
 * duración-- que el backend declara en `columns[].semantic` y que el dataset
 * no conoce.
 *
 * Todo en español de Ecuador (`Intl.*(..., "es-EC")`): es el dominio de este
 * producto (Ecuador, USD).
 */

const CURRENCY = new Intl.NumberFormat("es-EC", {
  style: "currency",
  currency: "USD",
});

const COUNT = new Intl.NumberFormat("es-EC", { maximumFractionDigits: 0 });

const NUMBER = new Intl.NumberFormat("es-EC", { maximumFractionDigits: 2 });

const DATETIME = new Intl.DateTimeFormat("es-EC", {
  dateStyle: "medium",
  timeStyle: "short",
});

const DATE_ONLY = new Intl.DateTimeFormat("es-EC", {
  dateStyle: "medium",
});

/** El valor de una celda: lo que trae `DatasetRow` (dataset.ts), nunca más. */
type CellValue = string | number | boolean | null;

const isNumber = (value: CellValue): value is number =>
  typeof value === "number";

/**
 * `percentage`: se asume que el dataset YA trae el número en la unidad "por
 * ciento" (ej. `42.5` → "42,5 %"), no en fracción (`0.425`).
 *
 * Por qué: es la misma convención que ya usa el resto de esta interfaz para
 * dinero y cantidades (`formatCell` en `dataset.ts` no reescala nada, solo
 * formatea lo que llega), y porque el protocolo (`PresentationColumnSchema`)
 * no declara una escala aparte -- si trajera la fracción, el backend tendría
 * que decirlo en algún lado y hoy no lo dice. Si esta suposición resulta
 * equivocada para algún `present_result` real, se nota de inmediato (el
 * número sale 100 veces más chico de lo esperado) y se corrige aquí.
 */
export const formatPercentage = (value: CellValue): string => {
  if (!isNumber(value)) return "—";
  return `${NUMBER.format(value)} %`;
};

export const formatCurrency = (value: CellValue): string => {
  if (!isNumber(value)) return "—";
  return CURRENCY.format(value);
};

export const formatDurationDays = (value: CellValue): string => {
  if (!isNumber(value)) return "—";
  const rounded = Math.round(value * 10) / 10;
  const label = rounded === 1 ? "día" : "días";
  return `${NUMBER.format(rounded)} ${label}`;
};

export const formatCount = (value: CellValue): string => {
  if (!isNumber(value)) return "—";
  return COUNT.format(value);
};

export const formatLabel = (value: CellValue): string => {
  if (value === null || value === "") return "—";
  return String(value);
};

export const formatEntityId = (value: CellValue): string => {
  if (value === null || value === "") return "—";
  return String(value);
};

/** Fecha y hora, para una columna `timestamp`. */
export const formatTimestamp = (value: CellValue): string => {
  if (typeof value !== "string" || value === "") return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return DATETIME.format(date);
};

/** Solo la fecha, sin hora: lo que pide "datos al" (`dataAsOf`). */
export const formatDateOnly = (value: CellValue): string => {
  if (typeof value !== "string" || value === "") return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return DATE_ONLY.format(date);
};

/** Una función por semántica, para el caso general de una columna cualquiera. */
export const formatBySemantic = (
  value: CellValue,
  semantic: ColumnSemantic,
): string => {
  switch (semantic) {
    case "currency":
      return formatCurrency(value);
    case "percentage":
      return formatPercentage(value);
    case "duration_days":
      return formatDurationDays(value);
    case "count":
      return formatCount(value);
    case "label":
      return formatLabel(value);
    case "timestamp":
      return formatTimestamp(value);
    case "entity_id":
      return formatEntityId(value);
  }
};
